import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Settings,
  Save,
  RefreshCw,
  Eye,
  Calendar,
  Hash,
  AlertCircle,
  CheckCircle,
  Info,
  BarChart3,
  Clock
} from 'lucide-react';

const KotSettings = () => {
  const { vendor } = useAuth();
  const queryClient = useQueryClient();
  const [kotPrefix, setKotPrefix] = useState('');
  const [previewPrefix, setPreviewPrefix] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Fetch KOT settings
  const { data: kotSettings, isLoading, error } = useQuery(
    'kot-settings',
    async () => {
      const response = await api.get('/vendor/kot-settings');
      return response.data.data;
    },
    {
      onSuccess: (data) => {
        setKotPrefix(data.kot_prefix);
        setPreviewPrefix(data.kot_prefix);
      }
    }
  );

  // Fetch KOT statistics
  const { data: kotStats } = useQuery(
    ['kot-stats', selectedDate],
    async () => {
      const response = await api.get(`/vendor/kot-settings/stats?date=${selectedDate}`);
      return response.data.data;
    }
  );

  // Generate next KOT preview
  const { data: nextKotPreview } = useQuery(
    ['next-kot-preview', previewPrefix],
    async () => {
      const response = await api.get(`/vendor/kot-settings/next?custom_prefix=${previewPrefix}`);
      return response.data.data;
    },
    {
      enabled: !!previewPrefix
    }
  );

  // Update KOT prefix mutation
  const updatePrefixMutation = useMutation(
    async (newPrefix) => {
      const response = await api.put('/vendor/kot-settings/prefix', {
        kot_prefix: newPrefix
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kot-settings');
        queryClient.invalidateQueries('next-kot-preview');
        alert('KOT prefix updated successfully!');
      },
      onError: (error) => {
        alert(error.response?.data?.message || 'Failed to update KOT prefix');
      }
    }
  );

  // Reset daily sequence mutation
  const resetSequenceMutation = useMutation(
    async () => {
      const response = await api.post('/vendor/kot-settings/reset');
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kot-settings');
        queryClient.invalidateQueries('kot-stats');
        alert('Daily sequence reset successfully!');
      },
      onError: (error) => {
        alert(error.response?.data?.message || 'Failed to reset sequence');
      }
    }
  );

  const handlePrefixChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    setKotPrefix(value);
    setPreviewPrefix(value);
  };

  const handleSave = () => {
    if (!kotPrefix.trim()) {
      alert('KOT prefix cannot be empty');
      return;
    }
    updatePrefixMutation.mutate(kotPrefix);
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset today\'s sequence? This will start the count from 001 again.')) {
      resetSequenceMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800">Failed to load KOT settings</span>
        </div>
      </div>
    );
  }

  return (
    <div className=" ">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-secondary-900 flex items-center space-x-2">
            <Settings className="h-6 w-6 md:h-7 md:w-7" />
            <span>KOT Settings</span>
          </h1>
          <p className="text-sm md:text-base text-secondary-600 mt-1">
            Manage your Kitchen Order Ticket (KOT) reference numbers
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* KOT Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center space-x-2">
            <Hash className="h-5 w-5" />
            <span>KOT Configuration</span>
          </h2>

          <div className="space-y-4">
            {/* Current Settings Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Current Settings:</p>
                  <p>Prefix: <span className="font-mono font-bold">{kotSettings?.kot_prefix}</span></p>
                  <p>Next KOT: <span className="font-mono font-bold text-primary-600">{kotSettings?.next_kot_reference}</span></p>
                  <p>Today's Count: <span className="font-bold">{kotSettings?.last_sequence_number || 0}</span></p>
                </div>
              </div>
            </div>

            {/* Prefix Input */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                KOT Prefix
              </label>
              <input
                type="text"
                value={kotPrefix}
                onChange={handlePrefixChange}
                placeholder="Enter prefix (e.g., DESSERTDAN)"
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono"
                maxLength={20}
              />
              <p className="text-xs text-secondary-500 mt-1">
                Only letters, numbers, and underscores allowed. Max 20 characters.
              </p>
            </div>

            {/* Preview */}
            {nextKotPreview && (
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Eye className="h-4 w-4 text-secondary-600" />
                  <span className="text-sm font-medium text-secondary-700">Preview</span>
                </div>
                <div className="font-mono text-lg font-bold text-primary-600">
                  {nextKotPreview.kot_reference}
                </div>
                <p className="text-xs text-secondary-500 mt-1">
                  {nextKotPreview.is_new_day ? 'New day - sequence will start from 001' : `Next sequence: ${nextKotPreview.sequence}`}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={handleSave}
                disabled={updatePrefixMutation.isLoading || kotPrefix === kotSettings?.kot_prefix}
                className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors"
              >
                {updatePrefixMutation.isLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>Save Changes</span>
              </button>

              <button
                onClick={handleReset}
                disabled={resetSequenceMutation.isLoading}
                className="flex-1 bg-secondary-600 text-white px-4 py-2 rounded-lg hover:bg-secondary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors"
              >
                {resetSequenceMutation.isLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span>Reset Today's Count</span>
              </button>
            </div>
          </div>
        </div>

        {/* KOT Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>KOT Statistics</span>
          </h2>

          <div className="space-y-4">
            {/* Date Selector */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Select Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Statistics Cards */}
            {kotStats && (
              <div className="space-y-3">
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-primary-600 font-medium">Total Orders</p>
                      <p className="text-2xl font-bold text-primary-900">{kotStats.total_orders}</p>
                    </div>
                    <Calendar className="h-8 w-8 text-primary-600" />
                  </div>
                </div>

                {kotStats.last_kot_reference && (
                  <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-secondary-600 font-medium">Last KOT Reference</p>
                        <p className="text-lg font-mono font-bold text-secondary-900">{kotStats.last_kot_reference}</p>
                      </div>
                      <Hash className="h-8 w-8 text-secondary-600" />
                    </div>
                  </div>
                )}

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">Current Prefix</p>
                      <p className="text-lg font-mono font-bold text-green-900">{kotStats.kot_prefix}</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                </div>

                {kotStats.is_today && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-5 w-5 text-blue-600" />
                      <span className="text-sm text-blue-800 font-medium">
                        This is today's data (live updates)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Information Section */}
      <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-3">How KOT References Work</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-secondary-700">
          <div>
            <h4 className="font-medium text-secondary-900 mb-2">📋 Format</h4>
            <p>KOT references follow the format: <span className="font-mono bg-white px-2 py-1 rounded">PREFIX_001</span></p>
            <p className="mt-1">Example: <span className="font-mono bg-white px-2 py-1 rounded text-primary-600">DESSERTDAN_001</span></p>
          </div>
          <div>
            <h4 className="font-medium text-secondary-900 mb-2">🔄 Daily Reset</h4>
            <p>The sequence number resets to 001 every day automatically.</p>
            <p className="mt-1">You can also manually reset if needed.</p>
          </div>
          <div>
            <h4 className="font-medium text-secondary-900 mb-2">✏️ Customizable</h4>
            <p>You can change the prefix anytime from this page.</p>
            <p className="mt-1">Only letters, numbers, and underscores are allowed.</p>
          </div>
          <div>
            <h4 className="font-medium text-secondary-900 mb-2">📊 Tracking</h4>
            <p>View statistics for any date to see order counts.</p>
            <p className="mt-1">Each order gets a unique KOT reference automatically.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KotSettings;